import type {AppState} from '@/types'
import {filter} from 'ramda'
import fileDownloader from '@/file-downloader'

const getNewTabDataFlags = (tab) => {
  let tabDataFlag = {}
  switch (tab) {
    case 'home':
      tabDataFlag['isHomeDataLoaded'] = true
      break
    case 'portfolios':
      tabDataFlag['isPortfoliosDataLoaded'] = true
      break
    case 'settings':
      tabDataFlag['isSettingsDataLoaded'] = true
      break
    case 'exports':
      tabDataFlag['isExportsDataLoaded'] = true
      break
    case 'analysis':
      tabDataFlag['isAnalysisDataLoaded'] = true
      break
    default:
      tabDataFlag['isHomeDataLoaded'] = true
  }
  return tabDataFlag
}
const initialState: AppState = {
  activePortfolioId: undefined,
  translations: {},
  notifications: [],
  user: {
    countryCode: '',
    languageCode: '',
    preferredLanguageCode: '',
    userId: 0, // refers csUserId
    homeUrl: '',
    serviceBalanceInternational: 0,
    serviceUsedInternational: 0,
    serviceSubscribedInternational: 0,
    id: 0, // refers userId
  },
  preferences: {
    autoRiskTracker: true,
  },
  showAppLoader: false,
  tabDataFlags: {
    isHomeDataLoaded: false,
    isPortfoliosDataLoaded: false,
    isSettingsDataLoaded: false,
    isExportsDataLoaded: false,
    isAnalysisDataLoaded: false,
  },
  customer: {
    users: [],
    usersLoader: false,
    updateLoader: false,
  },
  eventsExport: [],
}

const fetchActiveUsers = (list) => {
  let isActive = (user) => user.userName !== null
  let users = filter(isActive, list)
  return users
}

export default (state: AppState = initialState, action): AppState => {
  const {type, payload} = action

  switch (type) {
    case 'FETCH_INITIAL_DATA':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'FETCH_INITIAL_DATA_SUCCESS':
      const activePortfolio = payload.portfoliosObj ? payload.portfoliosObj.activePortfolio : {}
      let miniUserData = initialState.user
      if (payload.user) {
        miniUserData = {
          ...initialState.user,
          id: payload.user.userId,
          countryCode: payload.user.countryCode,
          languageCode: payload.user.languageCode,
          preferredLanguageCode: payload.user.languageCode || 'GB',
          userId: payload.user.csUserId,
          homeUrl: payload.user.homeUrl,
          serviceBalanceInternational: payload.user.serviceBalanceInternational !== null ? payload.user.serviceBalanceInternational : 0,
          serviceUsedInternational: payload.user.serviceUsedInternational !== null ? payload.user.serviceUsedInternational : 0,
          serviceSubscribedInternational: payload.user.serviceSubscribedInternational !== null ? payload.user.serviceSubscribedInternational : 0,
        }
      }
      return {
        ...state,
        showAppLoader: false,
        activePortfolioId: activePortfolio.portfolioId,
        translations: payload.translations || {},
        notifications: payload.notifications ? payload.notifications.data : [],
        user: miniUserData,
        preferences: payload.preferences || initialState.preferences,
      }
    case 'FETCH_INITIAL_DATA_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'CHANGE_ACTIVE_PORTFOLIO':
      return {
        ...state,
        activePortfolioId: action.portfolioId,
        tabDataFlags: {
          ...initialState.tabDataFlags,
        },
      }
    case 'DELETE_PORTFOLIO':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'DELETE_PORTFOLIO_SUCCESS':
      return {
        ...state,
        activePortfolioId: action.newActiveId,
        showAppLoader: false,
        tabDataFlags: {
          ...initialState.tabDataFlags,
        },
      }
    case 'DELETE_PORTFOLIO_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'FETCH_NOTIFICATIONS_SUCCESS':
      return {
        ...state,
        notifications: payload.data,
      }
    case 'FETCH_USER_SUCCESS':
      const {user = {}} = payload

      const miniUser = {
        countryCode: user.countryCode,
        languageCode: user.languageCode,
        preferredLanguageCode: user.languageCode || 'GB',
        userId: user.csUserId,
        homeUrl: user.homeUrl,
        serviceBalanceInternational: user.serviceBalanceInternational !== null ? user.serviceBalanceInternational : 0,
        serviceUsedInternational: user.serviceUsedInternational !== null ? user.serviceUsedInternational : 0,
        serviceSubscribedInternational: user.serviceSubscribedInternational !== null ? user.serviceSubscribedInternational : 0,
      }

      return {
        ...state,
        user: miniUser,
      }
    case 'FETCH_USER_PREFERENCES_SUCCESS':
      return {
        ...state,
        preferences: payload.preferences,
      }
    case 'SAVE_USER_PREFERENCES_SUCCESS':
      return {
        ...state,
        preferences: {
          autoRiskTracker: action.autoRiskTracker,
        },
      }
    case 'IMPORT_COMPANIES_FILE':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'IMPORT_COMPANIES_FILE_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'IMPORT_COMPANIES_FILE_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...initialState.tabDataFlags,
        },
      }
    case 'SYNC_COMPANIES_FILE':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'SYNC_COMPANIES_FILE_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...initialState.tabDataFlags,
        },
      }
    case 'SYNC_COMPANIES_FILE_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'DELETE_PORTFOLIO_COMPANIES':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'DELETE_PORTFOLIO_COMPANIES_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...initialState.tabDataFlags,
        },
      }
    case 'DELETE_PORTFOLIO_COMPANIES_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'FETCH_PORTFOLIO_COMPANIES':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'FETCH_PORTFOLIO_COMPANIES_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'FETCH_PORTFOLIO_COMPANIES_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'FETCH_PORTFOLIO_COMPANIES_BY_RISK_TYPE':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'FETCH_PORTFOLIO_COMPANIES_BY_RISK_TYPE_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'FETCH_PORTFOLIO_COMPANIES_BY_RISK_TYPE_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'SAVE_LANG_PREFERENCE':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'SAVE_LANG_PREFERENCE_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        translations: action.payload.translations,
        user: {
          ...state.user,
          preferredLanguageCode: action.language,
        },
      }
    case 'SAVE_LANG_PREFERENCE_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'ADD_PORTFOLIO':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'ADD_PORTFOLIO_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'ADD_PORTFOLIO_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'GET_PORTFOLIO_DATA':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'GET_PORTFOLIO_DATA_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'GET_PORTFOLIO_DATA_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'UPDATE_PORTFOLIO':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'UPDATE_PORTFOLIO_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'UPDATE_PORTFOLIO_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'ADD_EXPORT':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'ADD_EXPORT_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'ADD_EXPORT_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'SAVE_EXPORT':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'SAVE_EXPORT_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'SAVE_EXPORT_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'UPDATE_COMPANY_DETAILS':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'UPDATE_COMPANY_DETAILS_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'UPDATE_COMPANY_DETAILS_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'DELETE_EXPORT':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'DELETE_EXPORT_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'DELETE_EXPORT_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'RESET_TAB_DATA_FLAGS':
      return {
        ...state,
        tabDataFlags: {
          ...initialState.tabDataFlags,
        },
      }
    case 'SET_TAB_DATA_FLAGS':
      return {
        ...state,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags(payload.tab),
        },
      }
    case 'FETCH_HOME_TAB_DATA':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'FETCH_HOME_TAB_DATA_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('home'),
        },
      }
    case 'FETCH_HOME_TAB_DATA_FAILURE':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('home'),
        },
      }
    case 'FETCH_PORTFOLIOS_TAB_DATA':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'FETCH_PORTFOLIOS_TAB_DATA_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('portfolios'),
        },
        user: {
          ...state.user,
          serviceUsedInternational: payload.companies.serviceUsedInternational !== null ? Number(payload.companies.serviceUsedInternational) : 0,
          serviceSubscribedInternational: payload.companies.serviceSubscribedInternational !== null ? Number(payload.companies.serviceSubscribedInternational) : 0,
        },
      }
    case 'FETCH_PORTFOLIOS_TAB_DATA_FAILURE':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('portfolios'),
        },
      }
    case 'FETCH_SETTINGS_TAB_DATA':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'FETCH_SETTINGS_TAB_DATA_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        activePortfolioId: action.portfolioId,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('settings'),
        },
      }
    case 'FETCH_SETTINGS_TAB_DATA_FAILURE':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('settings'),
        },
      }
    case 'FETCH_EXPORTS_TAB_DATA':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'FETCH_EXPORTS_TAB_DATA_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        activePortfolioId: action.portfolioId,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('exports'),
        },
      }
    case 'FETCH_EXPORTS_TAB_DATA_FAILURE':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('exports'),
        },
      }
    case 'FETCH_ANALYSIS_TAB_DATA':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'FETCH_ANALYSIS_TAB_DATA_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('analysis'),
        },
      }
    case 'FETCH_ANALYSIS_TAB_DATA_FAILURE':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...state.tabDataFlags,
          ...getNewTabDataFlags('analysis'),
        },
      }
    case 'COPY_PORTFOLIO_COMPANIES':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'COPY_PORTFOLIO_COMPANIES_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'COPY_PORTFOLIO_COMPANIES_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'MOVE_PORTFOLIO_COMPANIES':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'MOVE_PORTFOLIO_COMPANIES_SUCCESS':
      return {
        ...state,
        showAppLoader: false,
        tabDataFlags: {
          ...initialState.tabDataFlags,
        },
      }
    case 'MOVE_PORTFOLIO_COMPANIES_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'GET_PORTFOLIOS_DATA':
      return {
        ...state,
        showAppLoader: true,
      }
    case 'GET_PORTFOLIOS_DATA_SUCCESS':
      const activePortf = payload.portfoliosObj ? payload.portfoliosObj.activePortfolio : {}
      return {
        ...state,
        activePortfolioId: activePortf.portfolioId,
        showAppLoader: false,
        tabDataFlags: {
          ...initialState.tabDataFlags,
        },
        notifications: payload.notifications ? (payload.notifications.data || []) : state.notifications,
      }
    case 'GET_PORTFOLIOS_DATA_FAILURE':
      return {
        ...state,
        showAppLoader: false,
      }
    case 'REFRESH_EXPORTS':
      return {
        ...state,
        tabDataFlags: {
          ...state.tabDataFlags,
          isExportsDataLoaded: false,
        },
      }
    case 'FETCH_CUSTOMER_USERS':
      return {
        ...state,
        customer: {
          ...state.customer,
          usersLoader: true,
          users: [],
        },
      }
    case 'FETCH_CUSTOMER_USERS_SUCCESS':
      return {
        ...state,
        customer: {
          ...state.customer,
          usersLoader: false,
          users: fetchActiveUsers(payload.data.userPermissions),
        },
      }
    case 'FETCH_CUSTOMER_USERS_FAILURE':
      return {
        ...state,
        customer: {
          ...state.customer,
          usersLoader: false,
        },
      }
    case 'UPDATE_CUSTOMER_USERS_PERMISSIONS':
      return {
        ...state,
        customer: {
          ...state.customer,
          updateLoader: true,
        },
      }
    case 'UPDATE_CUSTOMER_USERS_PERMISSIONS_SUCCESS':
      return {
        ...state,
        customer: {
          ...state.customer,
          updateLoader: false,
        },
      }
    case 'UPDATE_CUSTOMER_USERS_PERMISSIONS_FAILURE':
      return {
        ...state,
        customer: {
          ...state.customer,
          updateLoader: false,
        },
      }
    case 'FETCH_PORTFOLIO_EXPORT_EVENTS':
      return {
        ...state,
        showAppLoader: true,
        eventsExport: payload.data,
      }
    case 'FETCH_PORTFOLIO_EXPORT_EVENTS_SUCCESS':
      fileDownloader(payload.data)('exportNotifications', action.props)
      return {
        ...state,
        showAppLoader: false,
        eventsExport: payload.data,
      }
    case 'FETCH_PORTFOLIO_EXPORT_EVENTS_FAILURE':
      return {
        ...state,
        showAppLoader: false,
        errorMessage: payload.data,
      }
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/store/app/reducer.js